import { firestoreDb } from '$lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Checks assigned ticket numbers (numbers, not strings) against instant win prizes
 * on the client side and fetches the parent product's name.
 *
 * @param {object} assignedNumbers -
 *    An object where keys are prize IDs and values are arrays of assigned ticket numbers (numbers, not strings).
 * @returns {Promise<Array>} -
 *    An array of matched prizes with their details plus productName.
 */
export default async function checkInstantWins(assignedNumbers) {
	const matchedPrizes = [];
	const db = firestoreDb();

	try {
		// Build an array of promises for each product
		const fetchPromises = Object.entries(assignedNumbers).map(async ([prizeId, ticketNumbers]) => {
			const productDocRef = doc(db, 'products', prizeId);

			const productSnap = await getDoc(productDocRef);
			const productName = productSnap.exists() ? (productSnap.data()?.name ?? '') : '';

			// Fetch only the instant win docs matching the assigned ticket numbers
			const winDocs = await Promise.all(
				ticketNumbers.map((num) => {
					const ref = doc(db, 'products', prizeId, 'instantWins', num.toString());
					return getDoc(ref);
				})
			);

			winDocs.forEach((snap) => {
				if (snap.exists()) {
					const instantWin = snap.data();
					matchedPrizes.push({
						prizeId,
						productName,
						ticketNumber: instantWin.number,
						prize: instantWin.prize,
						claimed: instantWin.claimed,
						claimedAt: instantWin.claimedAt,
						claimedBy: instantWin.claimedBy,
						assignedAt: instantWin.assignedAt
					});
				}
			});
		});

		// Wait for all fetches to complete
		await Promise.all(fetchPromises);

		return matchedPrizes;
	} catch (error) {
		console.error('Error checking instant wins:', error);
		throw new Error('Failed to check instant wins');
	}
}
